import { useState } from "react";

import { css } from "@emotion/css";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useStyles } from "@/hooks/useStyles.ts";
import { doctorFormattedName } from "@/models/UserModel.ts";
import {
  AbsenceMessageFormData,
  absenceMessageFormSchema,
} from "@/pages/account/AbsenceMessage/AbsenceMessageFormSchema.ts";
import { ConfirmAbsenceMessageDialog } from "@/pages/account/AbsenceMessage/ConfirmAbsenceMessageDialog.tsx";
import { Queries } from "@/queries/Queries.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { Button } from "@/uiKit/atoms/Button.tsx";
import { Form } from "@/uiKit/atoms/forms/Form.tsx";
import { TextToast } from "@/uiKit/atoms/Toast.tsx";
import { Typography } from "@/uiKit/atoms/Typography.tsx";
import { ControlledTextField } from "@/uiKit/molecules/ControlledTextField.tsx";

export const AbsenceMessageBody = () => {
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);
  const me = Queries.user.usePractitionerMe({});
  const { mutate, isPending } = Queries.messaging.useBroadcastMessage();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const defaultMessage = t("pages.account.absenceMessage.template", {
    name: doctorFormattedName(me.lastName, me.firstName),
  });

  const formContext = useForm<AbsenceMessageFormData>({
    resolver: zodResolver(absenceMessageFormSchema),
    defaultValues: { contentText: defaultMessage },
  });

  const onConfirm = () => {
    mutate(
      { contentText: formContext.getValues("contentText") },
      {
        onSuccess: () => {
          setIsConfirmOpen(false);
          TextToast.success(
            t("pages.account.absenceMessage.successTitle"),
            t("pages.account.absenceMessage.successSubtitle"),
          );
        },
        onError: () => {
          TextToast.error(t("pages.account.absenceMessage.errorMessage"));
        },
      },
    );
  };

  return (
    <div className={styles.container}>
      <Typography variant="body">{t("pages.account.absenceMessage.helpText")}</Typography>
      <Form id="absence-message-form" formContext={formContext} onSubmit={() => setIsConfirmOpen(true)}>
        <ControlledTextField id="absence-message-content" name="contentText" multiline />
        <div className={styles.actions}>
          <Button type="submit" variant="contained" data-action="messaging.absence.send">
            {t("pages.account.absenceMessage.send")}
          </Button>
        </div>
      </Form>
      <ConfirmAbsenceMessageDialog
        open={isConfirmOpen}
        message={formContext.getValues("contentText")}
        isPending={isPending}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={onConfirm}
      />
    </div>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(8),
  }),
  actions: css({
    display: "flex",
    justifyContent: "flex-end",
  }),
});
